import { BaseEntity } from '../entities/base.entity';
import { Store } from '@ngxs/store';
import { ApiService } from '../jsonApi/api.service';
import { inject, Injectable } from '@angular/core';
import { firstValueFrom, Observable, tap } from 'rxjs';
import { map } from 'rxjs/operators';
import { Create, Delete, EntityStateModel, LoadMany, LoadSingle, StoreMeta, Update } from '../state/entity.state';
import { toJsonApi } from '../../utils/entity-states/json-api-converter';
import { makeQueryKey } from '../../utils/entity-states/query-key';

@Injectable({ providedIn: 'root' })
export abstract class EntityService<T extends BaseEntity> {
  protected resourceType!: string;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  stateClass!: any;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  protected relationServices: Record<string, () => EntityService<any>> = {};

  protected store = inject(Store);
  protected api = inject(ApiService);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  protected registerRelation(relationName: string, service: EntityService<any>) {
    this.relationServices[relationName] = () => service;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  protected registerLazyRelation(relationName: string, serviceFactory: () => EntityService<any>) {
    this.relationServices[relationName] = serviceFactory;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  async ensureLoaded(query: Record<string, any> = {}): Promise<void> {
    const state = this.store.selectSnapshot(this.stateClass) as EntityStateModel<T>;
    const queryKey = makeQueryKey(query);

    if (state?.loadedQueries?.[queryKey]) return;

    const normalized = await firstValueFrom(this.api.getData(this.resourceType, query));

    const mainEntities = normalized[this.resourceType] as Record<string, BaseEntity>;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const mainStateName = (this.stateClass as any).stateName;
    const mainResourceType = this.resourceType;
    this.store.dispatch(
      new LoadMany({
        entities: mainEntities,
        query,
        stateName: mainStateName,
        relationKeys: Object.keys(this.relationServices),
      })
    );

    for (const [relationKey, getService] of Object.entries(this.relationServices)) {
      const service = getService();
      if (normalized[relationKey]) {
        const entities = normalized[relationKey] as Record<string, BaseEntity>;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const stateName = (service.stateClass as any).stateName;

        // relationKeys: empty array here, because these are nested entities themselves
        this.store.dispatch(
          new LoadMany({ entities, query: { from: mainResourceType }, stateName, relationKeys: [] })
        );
      }
    }

    if (normalized.meta) {
      this.store.dispatch(new StoreMeta(queryKey, normalized.meta, mainStateName));
    }
  }

  /** Ensure a single entity is loaded */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  async ensureSingleLoaded(id: string, query: Record<string, any> = {}): Promise<void> {
    const state = this.store.selectSnapshot(this.stateClass) as EntityStateModel<T>;
    const queryKey = makeQueryKey(query);
    const loadedKey = `${id}:${queryKey}`;

    if (state?.loadedIds?.[loadedKey]) return;

    const normalized = await firstValueFrom(
      this.api.getDataSingle(this.resourceType, id, query)
    );

    const mainEntities = normalized[this.resourceType] as Record<string, BaseEntity>;
    const mainEntity = mainEntities[id];

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const mainStateName = (this.stateClass as any).stateName;
    const mainResourceType = this.resourceType;

    // store main entity
    this.store.dispatch(
      new LoadSingle({
        entity: mainEntity,
        id,
        query,
        stateName: mainStateName,
        relationKeys: Object.keys(this.relationServices),
      })
    );

    // store included relations
    for (const [relationKey, getService] of Object.entries(this.relationServices)) {
      const service = getService();
      if (normalized[relationKey]) {
        const entities = normalized[relationKey] as Record<string, BaseEntity>;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const stateName = (service.stateClass as any).stateName;

        this.store.dispatch(
          new LoadMany({
            entities,
            query: { from: mainResourceType, id },
            stateName,
            relationKeys: [],
          })
        );
      }
    }

    if (normalized.meta) {
      this.store.dispatch(
        new StoreMeta(queryKey, normalized.meta, mainStateName)
      );
    }

    await this.afterLoad(normalized, query);
  }

  /** Create a new entity */
  async create(data: Partial<T>): Promise<T> {
    const payload = toJsonApi(this.resourceType, data);

    // Call API
    const normalized = await firstValueFrom(this.api.createDataSingle(this.resourceType, payload));

    // Extract entity from JSON:API response
    const entity = normalized[this.resourceType][Object.keys(normalized[this.resourceType])[0]] as T;

    // Dispatch NGXS Create action
    this.store.dispatch(new Create(entity));

    return entity;
  }

  /** Update an existing entity */
  update(entity: T): Observable<T> {
    const payload = toJsonApi(this.resourceType, entity);

    return this.api.updateDataSingle(this.resourceType, entity.id, payload).pipe(
      tap(normalized => {
        const updated = normalized[this.resourceType][entity.id];
        if (updated) {
          this.store.dispatch(new Update(updated));
        }
      }),
      map(normalized => normalized[this.resourceType][entity.id])
    );
  }

  /** Delete an entity */
  delete(id: string): Observable<void> {
    return this.api.deleteDataSingle(this.resourceType, id).pipe(
      tap(() => {
        // Dispatch Delete anyway; the action can handle missing entities
        this.store.dispatch(new Delete(id));
      })
    );
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unused-vars
  protected afterLoad(normalized: Record<string, any>, query?: Record<string, any>): Promise<void> | void {
    return;
  }
}
